import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputBase,
  Skeleton,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { MONO, Pill, rarityHex } from '../MemberPortal/Marketplace/components/cardKit';
import { cardArtUrl, cardTitle, cardArtFallback } from '../MemberPortal/NFTCollection/components/CardInspectModal';
import {
  asFaction,
  asRarity,
  asType,
  CatalogCard,
  FACTIONS,
  filterCards,
  RARITIES,
  TYPES
} from './filterCards';

/* Every card in the game, on the public /digital-collectibles page.
 *
 * Public data from the main API, fetched with a plain request rather than through
 * useNetwork(): nobody reading this page is signed in, and the shared client would
 * bring its auth interceptors along for a call that needs none. Never the game
 * server — the site talks to the main API only.
 *
 * The art is the portal's own S3 copy (cardArtUrl — imported, not copied, so this
 * page and the collection grid cannot drift apart), and every card is a full-size
 * 700×1150 PNG. So nothing here may ask for all ~1,100 of them at once: tiles have
 * the card's shape before the art lands, so nothing shifts; images load lazily
 * behind a skeleton; and cards enter the DOM a page at a time.
 *
 * The filters live in the URL (?q=&rarity=&type=&faction=) so a filtered view
 * can be shared and survives a reload. The pill colours are the marketplace's
 * (cardKit), so a rarity reads the same here as it does where the card is
 * bought and sold.
 */

const PAGE = 48; // cards added to the grid per step
const SEARCH_DEBOUNCE_MS = 250;
const SKELETONS = 12;

const SEARCH_ID = 'card-catalog-search';
const TITLE_ID = 'card-catalog-title';

const CYAN = '#38e8ff';
const INK = '#eef6fb';
const BODY = '#9fb2c0';
const DIM = '#8ea3b3';

const TYPE_LABEL: Record<string, string> = {
  character: 'Character',
  influencer: 'Influencer',
  token: 'Token'
};

const fmt = (n: number) => n.toLocaleString('en-AU');

const kickerSx = {
  font: `800 11px ${MONO}`,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: CYAN
} as const;

// Field labels — the collection filter's mono kicker (NFTFilter).
const labelSx = {
  display: 'block',
  mb: 1,
  font: `800 10px ${MONO}`,
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: DIM
} as const;

// The utility boxes' surface (DigitalCollectibles.tsx), so the catalog reads as
// part of the same page rather than something dropped onto it.
const panelSx = {
  borderRadius: '16px',
  background: 'linear-gradient(180deg, rgba(13,20,29,0.9), rgba(7,11,17,0.95))',
  border: '1px solid rgba(140,170,190,0.14)'
} as const;

const tileSx = {
  ...panelSx,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  p: { xs: 1, md: 1.25 }
} as const;

// minmax(0, 1fr), not 1fr: a long name must clamp, not widen its column.
const gridSx = {
  listStyle: 'none',
  m: 0,
  p: 0,
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(3, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    lg: 'repeat(5, minmax(0, 1fr))'
  },
  gap: { xs: 1.5, md: 2 }
} as const;

const focusRingSx = {
  '&.Mui-focusVisible': { outline: `2px solid ${CYAN}`, outlineOffset: '2px' }
} as const;

// The quiet cyan outline the TCG page uses for its secondary action.
const outlineBtnSx = {
  px: 3.5,
  py: 1.1,
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 800,
  fontSize: 15,
  color: CYAN,
  border: `1px solid ${CYAN}66`,
  '&:hover': { background: `${CYAN}14`, borderColor: CYAN },
  ...focusRingSx
} as const;

const skeletonSx = {
  bgcolor: 'rgba(140,170,190,0.08)',
  '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none' } }
} as const;

const nameSx = {
  mt: 1.25,
  px: 0.25,
  fontSize: { xs: 13.5, md: 14 },
  fontWeight: 800,
  lineHeight: 1.25,
  color: INK,
  overflowWrap: 'anywhere',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
} as const;

const typeSx = {
  font: `700 10px ${MONO}`,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DIM,
  whiteSpace: 'nowrap'
} as const;

/* The hover lift for the panels on this page that answer the pointer: the card
 * tiles here and the Trading Card Game tile in DigitalCollectibles.tsx, so the two
 * move the same way. Only where there is a real pointer — a tap would leave a
 * phone's tile stuck in the air — and not at all for anyone who asked for less
 * motion. */
export const liftSx = {
  transition: 'transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
  '@media (hover: hover)': {
    '&:hover': {
      transform: 'translateY(-4px)',
      borderColor: 'rgba(56,232,255,0.45)',
      boxShadow: '0 22px 50px -28px rgba(56,232,255,0.35)'
    }
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' }
  }
} as const;

type Status = 'loading' | 'ready' | 'error';

/* Each row is checked into shape here, so a malformed one is dropped rather than
 * taking the grid down. The values are kept exactly as sent: cardArtUrl builds
 * the S3 path from them and has to build the same one the portal does. */
const toCard = (raw: unknown): CatalogCard | null => {
  const c = (raw || {}) as Record<string, unknown>;
  if (typeof c.name !== 'string' || !c.name) return null;
  const rarity = String(c.rarity ?? '');
  return {
    id: typeof c.id === 'number' || typeof c.id === 'string' ? c.id : `${c.name}|${rarity}`,
    name: c.name,
    rarity,
    type: String(c.type ?? ''),
    faction: String(c.faction ?? '')
  };
};

function useCatalog() {
  const [cards, setCards] = useState<CatalogCard[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL;
    if (!base) {
      setStatus('error');
      return undefined;
    }
    const ctrl = new AbortController();
    setStatus('loading');
    // No credentials: the list is the same for everyone, and a cookie would only
    // tie a public, cacheable read to whoever happens to be signed in.
    fetch(`${base}/tcg-public/cards`, { credentials: 'omit', signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: { cards?: unknown } | null) => {
        if (!d || !Array.isArray(d.cards)) throw new Error('no cards in the response');
        setCards(d.cards.map(toCard).filter((c): c is CatalogCard => c !== null));
        setStatus('ready');
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setStatus('error');
      });
    return () => ctrl.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { cards, status, retry };
}

/* A single-choice filter button. aria-pressed rather than a radio group because
 * pressing the chosen one again lets it go (back to All). */
const FilterChip = ({
  label,
  pressed,
  dot,
  onClick
}: {
  label: string;
  pressed: boolean;
  dot?: string;
  onClick: () => void;
}) => {
  const accent = dot || CYAN;
  return (
    <ButtonBase
      disableRipple
      aria-pressed={pressed}
      onClick={onClick}
      sx={{
        gap: 0.75,
        minHeight: 34,
        px: 1.5,
        borderRadius: 999,
        font: `700 11.5px ${MONO}`,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: pressed ? INK : BODY,
        border: `1px solid ${pressed ? accent : 'rgba(140,170,190,0.22)'}`,
        background: pressed ? `${accent}24` : 'rgba(255,255,255,0.02)',
        transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': { color: INK, borderColor: pressed ? accent : 'rgba(56,232,255,0.45)' },
        ...focusRingSx
      }}
    >
      {dot && (
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: '50%',
            background: dot,
            boxShadow: `0 0 8px ${dot}88`
          }}
        />
      )}
      {label}
    </ButtonBase>
  );
};

const ChipGroup = ({
  id,
  label,
  children
}: {
  id: string;
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography id={id} sx={labelSx}>
      {label}
    </Typography>
    <Box role="group" aria-labelledby={id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {children}
    </Box>
  </Box>
);

/* One card. The art is asked for only as it nears the screen, decoded off the main
 * thread, and faded in over a skeleton in a box that already has the card's shape.
 * If S3 has no art for it, the rarity orb stands in (as in the marketplace's
 * NftArt) rather than a broken-image icon; the name is still there beneath.
 * Memoised: typing in the search re-renders the catalog, and a page of tiles
 * whose cards have not changed has no reason to follow. */
const CardTile = memo(function CardTile({ card }: { card: CatalogCard }) {
  const [art, setArt] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const color = rarityHex(card.rarity);

  return (
    <Box component="li" sx={[tileSx, liftSx]}>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '700 / 1150',
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #0e1622, #070c13)'
        }}
      >
        {art === 'loading' && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ ...skeletonSx, position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
        )}
        {art === 'failed' ? (
          <Box
            aria-hidden
            sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}
          >
            <Box
              sx={{
                width: '40%',
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: `radial-gradient(circle at 42% 38%, ${color}, #0b2740 72%)`,
                boxShadow: `0 0 30px ${color}55`
              }}
            />
          </Box>
        ) : (
          <Box
            component="img"
            src={cardArtUrl(card)}
            alt={card.name}
            loading="lazy"
            decoding="async"
            onLoad={() => setArt('loaded')}
            onError={(e) => { if (!cardArtFallback(e.currentTarget)) setArt('failed'); }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity: art === 'loaded' ? 1 : 0,
              transition: 'opacity 0.3s ease'
            }}
          />
        )}
      </Box>
      <Typography title={cardTitle(card.name)} sx={nameSx}>
        {cardTitle(card.name)}
      </Typography>
      <Box
        sx={{
          mt: 'auto',
          pt: 1,
          px: 0.25,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1,
          rowGap: 0.5
        }}
      >
        <Pill color={color}>{card.rarity}</Pill>
        <Typography component="span" sx={typeSx}>
          {TYPE_LABEL[card.type.toLowerCase()] || card.type}
        </Typography>
      </Box>
    </Box>
  );
});

const SkeletonGrid = () => (
  <Box component="ul" aria-hidden sx={gridSx}>
    {Array.from({ length: SKELETONS }, (_, i) => (
      <Box component="li" key={i} sx={tileSx}>
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            ...skeletonSx,
            width: '100%',
            height: 'auto',
            aspectRatio: '700 / 1150',
            borderRadius: '10px'
          }}
        />
        <Skeleton variant="text" animation="wave" sx={{ ...skeletonSx, mt: 1, width: '72%' }} />
        <Skeleton variant="text" animation="wave" sx={{ ...skeletonSx, width: '44%' }} />
      </Box>
    ))}
  </Box>
);

export default function CardCatalog() {
  const { cards, status, retry } = useCatalog();
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlQ = (params.get('q') || '').trim();
  const rarity = asRarity(params.get('rarity'));
  const type = asType(params.get('type'));
  const faction = asFaction(params.get('faction'));

  /* Reads the LIVE query string, not `params`: react-router's functional updater
     hands back the snapshot from the last render, so a chip clicked inside the
     search box's debounce window would be undone by the write that follows it.
     replace, not push — a filter is not a page, so Back still leaves the page —
     and anything else on the URL (utm_*, a referral code) is kept. */
  const writeParams = useCallback(
    (patch: Partial<Record<'q' | 'rarity' | 'type' | 'faction', string>>) => {
      const now = new URLSearchParams(window.location.search);
      const next = new URLSearchParams(now);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (next.toString() === now.toString()) return;
      navigate({ search: next.toString() ? `?${next}` : '' }, { replace: true });
    },
    [navigate]
  );

  /* The box shows what is typed; the grid follows the URL, a beat behind. */
  const searchRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(urlQ);
  const committedQ = useRef(urlQ); // the q this component last put in, or took from, the URL

  const commitQ = useCallback(
    (value: string) => {
      committedQ.current = value.trim();
      writeParams({ q: committedQ.current });
    },
    [writeParams]
  );

  // The URL changed without us — the header's "Digital Collectibles" link, say —
  // so the box takes its q, or it would show a search the grid is not applying.
  useEffect(() => {
    if (urlQ === committedQ.current) return;
    committedQ.current = urlQ;
    setDraft(urlQ);
  }, [urlQ]);

  useEffect(() => {
    if (draft.trim() === committedQ.current) return undefined;
    const t = window.setTimeout(() => commitQ(draft), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [draft, commitQ]);

  const pickRarity = (r: string) => writeParams({ rarity: r === rarity ? '' : r });
  const pickType = (t: string) => writeParams({ type: t === type ? '' : t });
  const pickFaction = (f: string) => writeParams({ faction: f === faction ? '' : f });

  const filtered = useMemo(
    () => filterCards(cards, { q: urlQ, rarity, type, faction }),
    [cards, urlQ, rarity, type, faction]
  );

  /* Cards enter the DOM a PAGE at a time, and a new filter starts again from the
     first page. Keyed on the filter rather than reset in an effect, so the grid
     never renders the old count against the new results first. */
  const filterKey = `${urlQ.toLowerCase()}\n${rarity}\n${type}\n${faction}`;
  const [paging, setPaging] = useState({ key: filterKey, count: PAGE });
  const shown = paging.key === filterKey ? paging.count : PAGE;
  const hasMore = status === 'ready' && shown < filtered.length;
  const loadMore = useCallback(
    () =>
      setPaging((p) => ({
        key: filterKey,
        count: (p.key === filterKey ? p.count : PAGE) + PAGE
      })),
    [filterKey]
  );

  /* ...and the next page arrives on its own as the button nears the screen. One
     observer per filter, acting only on the way IN: with the footer in view,
     scroll anchoring can hold the button on screen while a page lands above it,
     and an observer re-armed after every page would then pull in the whole
     catalog without the reader scrolling at all. The button stays for whatever
     the observer misses, and for browsers without one. */
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!hasMore || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  /* A shared or reloaded filtered link opens on the cards, not on the hero above
     them — unless the reader is already somewhere else on the page. */
  const sectionRef = useRef<HTMLElement>(null);
  const arrivedFiltered = useRef(Boolean(urlQ || rarity || type || faction));
  useEffect(() => {
    if (arrivedFiltered.current && window.scrollY < 10) {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, []);

  const active = Boolean(urlQ || rarity || type || faction || draft.trim());
  const clearAll = useCallback(
    (viaKeyboard: boolean) => {
      setDraft('');
      committedQ.current = '';
      writeParams({ q: '', rarity: '', type: '', faction: '' });
      /* The button pressed goes away with the filters, and focus would go with
         it; hand a keyboard user the search instead. Not on a tap — focusing an
         input opens a phone's keyboard. */
      if (viaKeyboard) searchRef.current?.focus();
    },
    [writeParams]
  );

  return (
    <Box
      component="section"
      ref={sectionRef}
      aria-labelledby={TITLE_ID}
      sx={{
        px: { xs: 2.5, md: 3 },
        pb: { xs: 6, md: 9 },
        // clear of the sticky site header when scrolled to
        scrollMarginTop: { xs: 80, md: 96 }
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={kickerSx}>Every card</Typography>
          <Typography
            id={TITLE_ID}
            component="h2"
            className="inc-orbitron"
            sx={{
              mt: 1.5,
              fontSize: { xs: 26, sm: 32, md: 38 },
              fontWeight: 900,
              lineHeight: 1.1,
              color: INK
            }}
          >
            Browse the full collection
          </Typography>
          <Typography
            sx={{
              mt: 1.75,
              maxWidth: 640,
              mx: 'auto',
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.6,
              color: BODY
            }}
          >
            Every Digital Collectible in the Incrypt universe — search by name, or
            narrow it down by rarity, type and faction.
          </Typography>
        </Box>

        {/* ---------------- controls ---------------- */}
        <Box sx={{ ...panelSx, mt: { xs: 3, md: 4 }, p: { xs: 2, md: 2.5 } }}>
          <Box component="label" htmlFor={SEARCH_ID} sx={labelSx}>
            Search
          </Box>
          <Box
            component="form"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              commitQ(draft); // Enter applies now rather than after the pause
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 52,
              px: 1.5,
              borderRadius: '12px',
              background: 'rgba(7, 11, 17, 0.75)',
              border: '1px solid rgba(56, 232, 255, 0.22)',
              transition: 'border-color 120ms, box-shadow 120ms',
              '&:focus-within': {
                borderColor: 'rgba(56, 232, 255, 0.6)',
                boxShadow: '0 0 0 1px rgba(56, 232, 255, 0.35)'
              }
            }}
          >
            <SearchIcon sx={{ color: DIM, mr: 1 }} />
            <InputBase
              id={SEARCH_ID}
              inputRef={searchRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && draft) {
                  e.preventDefault();
                  setDraft('');
                  commitQ('');
                }
              }}
              placeholder="Search by card name"
              inputProps={{
                maxLength: 60,
                autoComplete: 'off',
                spellCheck: false,
                enterKeyHint: 'search'
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                color: INK,
                fontFamily: MONO,
                // 16px or iOS zooms the page in on focus
                fontSize: 16,
                '& input::placeholder': { color: '#5c6b78', opacity: 1 }
              }}
            />
            {draft && (
              <IconButton
                aria-label="Clear search"
                onClick={() => {
                  setDraft('');
                  commitQ('');
                  searchRef.current?.focus();
                }}
                sx={{ p: '6px', color: DIM, '&:hover': { color: CYAN }, ...focusRingSx }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              mt: 2.25,
              display: 'grid',
              // Rarity and Type side by side from lg, Faction beneath Rarity: all
              // three in one row would need ~1,550px of chips in a ~1,060px panel.
              gridTemplateColumns: { xs: '1fr', lg: 'auto auto' },
              justifyContent: 'start',
              columnGap: 5,
              rowGap: 2
            }}
          >
            <ChipGroup id="card-catalog-rarity" label="Rarity">
              <FilterChip label="All" pressed={!rarity} onClick={() => pickRarity('')} />
              {RARITIES.map((r) => (
                <FilterChip
                  key={r}
                  label={r}
                  dot={rarityHex(r)}
                  pressed={rarity === r}
                  onClick={() => pickRarity(r)}
                />
              ))}
            </ChipGroup>
            <ChipGroup id="card-catalog-type" label="Type">
              <FilterChip label="All" pressed={!type} onClick={() => pickType('')} />
              {TYPES.map((t) => (
                <FilterChip
                  key={t}
                  label={TYPE_LABEL[t]}
                  pressed={type === t}
                  onClick={() => pickType(t)}
                />
              ))}
            </ChipGroup>
            <ChipGroup id="card-catalog-faction" label="Faction">
              <FilterChip label="All" pressed={!faction} onClick={() => pickFaction('')} />
              {FACTIONS.map((f) => (
                <FilterChip
                  key={f}
                  label={f}
                  pressed={faction === f}
                  onClick={() => pickFaction(f)}
                />
              ))}
            </ChipGroup>
          </Box>

          <Box
            sx={{
              mt: 2.25,
              pt: 1.75,
              minHeight: 34,
              borderTop: '1px solid rgba(140,170,190,0.12)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5
            }}
          >
            <Typography role="status" sx={{ font: `600 12.5px ${MONO}`, color: DIM }}>
              {status === 'loading'
                ? 'Loading cards…'
                : status === 'ready'
                ? `Showing ${fmt(filtered.length)} of ${fmt(cards.length)} cards`
                : ''}
            </Typography>
            {active && (
              <ButtonBase
                onClick={(e) => clearAll(e.detail === 0)}
                sx={{
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  font: `700 12px ${MONO}`,
                  letterSpacing: '0.06em',
                  color: CYAN,
                  '&:hover': { background: 'rgba(56,232,255,0.08)' },
                  ...focusRingSx
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
                Clear filters
              </ButtonBase>
            )}
          </Box>
        </Box>

        {/* ---------------- the cards ---------------- */}
        <Box sx={{ mt: { xs: 2.5, md: 3 } }}>
          {status === 'error' ? (
            <Box
              role="alert"
              sx={{ ...panelSx, py: { xs: 4, md: 5 }, px: 3, textAlign: 'center' }}
            >
              <Typography sx={{ color: INK, fontWeight: 700, fontSize: 16 }}>
                We couldn&rsquo;t load the cards.
              </Typography>
              <Button onClick={retry} sx={{ ...outlineBtnSx, mt: 2 }}>
                Try again
              </Button>
            </Box>
          ) : status === 'loading' ? (
            <SkeletonGrid />
          ) : filtered.length === 0 ? (
            <Box sx={{ ...panelSx, py: { xs: 4, md: 5 }, px: 3, textAlign: 'center' }}>
              <Typography sx={{ color: INK, fontWeight: 700, fontSize: 16 }}>
                {active ? 'No cards match.' : 'No cards to show yet.'}
              </Typography>
              {active && (
                <Button
                  onClick={(e) => clearAll(e.detail === 0)}
                  sx={{ ...outlineBtnSx, mt: 2 }}
                >
                  Clear filters
                </Button>
              )}
            </Box>
          ) : (
            // role="list" because Safari drops a list's semantics once its
            // bullets are styled away
            <Box component="ul" role="list" aria-labelledby={TITLE_ID} sx={gridSx}>
              {filtered.slice(0, shown).map((card) => (
                <CardTile key={card.id} card={card} />
              ))}
            </Box>
          )}

          {hasMore && (
            <Box
              ref={moreRef}
              sx={{
                mt: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Button onClick={loadMore} sx={outlineBtnSx}>
                Load more
              </Button>
              <Typography sx={{ font: `600 12px ${MONO}`, color: DIM }}>
                {fmt(shown)} of {fmt(filtered.length)} shown
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
